import { Controller } from "@hotwired/stimulus";
import { initReadMoreToggle } from '../helpers/init_read_more_toggle'

export default class extends Controller {
  static targets = ["input", "submit"]
  static values = {
    placeholderUrl: String
  }

  connect() {
    this.lastQuery = (this.inputTarget?.value || '').trim()
    this.isShowingQueryPlaceholders = false
    this.ensureHiddenField('page', '1')
    this.syncSearchTypeFromSidebar()
    initReadMoreToggle()

    // Initialize submit button state
    this.updateSubmitButton()

    // Add input event listener to update button state
    if (this.hasInputTarget) {
      this.inputTarget.addEventListener('input', () => this.updateSubmitButton())
    }

    // Keep hidden search_type + page in sync for any HTMX request that changes search_type
    document.addEventListener('htmx:beforeRequest', this.handleBeforeRequestBound = (event) => {
      const formEl = this.element
      const params = event.detail?.requestConfig?.parameters
      if (!params) return

      // Only show placeholders on form submissions (query changes), not filter-only updates
      if (event.detail.elt === formEl || event.detail.elt?.closest?.('#unified-search-form') === formEl) {
        const nextQuery = (params.query || '').trim()
        if (nextQuery && nextQuery !== this.lastQuery) {
          this.lastQuery = nextQuery
          this.showQueryLoadingPlaceholders()
        }
      }
    })

    document.addEventListener('htmx:afterSwap', this.afterSwapBound = (e) => {
      // Re-init readmore toggle in case new overview markup rendered
      if (e.target.id === 'search-results-column') initReadMoreToggle()
      if (!this.isShowingQueryPlaceholders) return
      if (this.isResultsSwap(e.target)) this.clearQueryLoadingPlaceholders()
    })

    window.addEventListener('search:loaded', this.searchLoadedBound = () => {
      if (this.isShowingQueryPlaceholders) this.clearQueryLoadingPlaceholders()
      initReadMoreToggle()
    })
  }

  disconnect() {
    document.removeEventListener('htmx:beforeRequest', this.handleBeforeRequestBound)
    document.removeEventListener('htmx:afterSwap', this.afterSwapBound)
    window.removeEventListener('search:loaded', this.searchLoadedBound)
  }

  // --- Placeholder helpers ---
  isResultsSwap(target) {
    if (!target) return false
    return target.id === 'search-results' ||
      target.querySelector?.('#search-results') ||
      target.id === 'search-results-column'
  }

  resultsColumn() {
    return document.getElementById('search-results-column')
  }

  showQueryLoadingPlaceholders() {
    const col = this.resultsColumn()
    if (!col || this.isShowingQueryPlaceholders) return
    if (this.hasPlaceholderUrlValue) {
      // Fetch placeholder partial via HTMX (innerHTML swap)
      htmx.ajax('GET', this.placeholderUrlValue, '#search-results-column')
      this.isShowingQueryPlaceholders = true
    }
  }

  clearQueryLoadingPlaceholders() {
    this.isShowingQueryPlaceholders = false
  }

  ensureHiddenField(name, value) {
    let el = this.element.querySelector(`input[name="${name}"]`)
    if (!el) {
      el = document.createElement('input')
      el.type = 'hidden'
      el.name = name
      this.element.appendChild(el)
    }
    el.value = value
    return el
  }

  currentSearchType() {
    const el = this.element.querySelector('input[name="search_type"]')
    return el ? el.value : 'all'
  }

  syncSearchTypeFromSidebar() {
    const sidebar = document.getElementById('ai-search-filters')
    if (!sidebar) return
    const value = sidebar.dataset.searchType || 'all'
    this.setSearchType(value)
  }

  setSearchType(value) {
    this.ensureHiddenField('search_type', value)
  }

  // Handle Enter key press in search input
  handleEnterKey(event) {
    if (event.key === 'Enter') {
      event.preventDefault()

      // Prevent submit if query is empty
      const query = (this.inputTarget?.value || '').trim()
      if (!query) {
        return false
      }

      // this prevents the typeahead from opening when the user switches browser tabs after searching
      event.target.blur()

      // The typeahead close is handled by the direct Stimulus action: keydown.enter->typeahead#close
      // We just need to submit the form here
      this.element.requestSubmit()
    }
  }

  // Update submit button state based on query input
  updateSubmitButton() {
    if (!this.hasSubmitTarget || !this.hasInputTarget) return

    const query = (this.inputTarget.value || '').trim()
    const isEmpty = query === ''

    // Set disabled attribute (disabled styling handled by browser)
    this.submitTarget.disabled = isEmpty
  }
}